import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  Alert,
  Modal,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  StatusBar,
  Platform,
  FlatList,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import DateTimePicker from '@react-native-community/datetimepicker';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width, height } = Dimensions.get('window');

export default function LeaveManagementScreen({ navigation }) {
  // Core state
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('requests'); // 'requests', 'balance', 'calendar', 'team'
  
  // Leave data
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState({});
  const [teamLeaves, setTeamLeaves] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);

  // UI state
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  // Form state
  const [newRequest, setNewRequest] = useState({
    type: '',
    startDate: new Date(),
    endDate: new Date(),
    reason: '',
    emergencyContact: '',
    handoverNotes: '',
    attachments: [],
  });
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Default leave types
  const defaultLeaveTypes = useMemo(() => [
    {
      id: 'annual',
      name: 'Annual Leave',
      description: 'Vacation and personal time off',
      color: '#4A90E2',
      icon: 'calendar-outline',
      maxDays: 25,
      requiresApproval: true,
      advanceNotice: 7, // days
    },
    {
      id: 'sick',
      name: 'Sick Leave',
      description: 'Medical leave and health-related absences',
      color: '#EF4444',
      icon: 'medical-bag',
      maxDays: 10,
      requiresApproval: false,
      advanceNotice: 0,
    },
    {
      id: 'personal',
      name: 'Personal Leave',
      description: 'Personal matters and family time',
      color: '#10B981',
      icon: 'person-outline',
      maxDays: 5,
      requiresApproval: true,
      advanceNotice: 3,
    },
    {
      id: 'emergency',
      name: 'Emergency Leave',
      description: 'Urgent and unforeseen circumstances',
      color: '#F59E0B',
      icon: 'alert-circle-outline',
      maxDays: 3,
      requiresApproval: false,
      advanceNotice: 0,
    },
    {
      id: 'maternity',
      name: 'Maternity/Paternity',
      description: 'Parental leave for new parents',
      color: '#8B5CF6',
      icon: 'heart-outline',
      maxDays: 90,
      requiresApproval: true,
      advanceNotice: 30,
    },
    {
      id: 'bereavement',
      name: 'Bereavement Leave',
      description: 'Time off for family loss',
      color: '#64748B',
      icon: 'flower-outline',
      maxDays: 5,
      requiresApproval: false,
      advanceNotice: 0,
    },
  ], []);

  // Initialize component
  useEffect(() => {
    initializeLeaveManagement();
  }, []);

  // Initialize leave management data
  const initializeLeaveManagement = useCallback(async () => {
    try {
      setIsLoading(true);

      // Load leave types
      const savedTypes = await AsyncStorage.getItem('leave_types');
      setLeaveTypes(savedTypes ? JSON.parse(savedTypes) : defaultLeaveTypes);

      // Load leave requests
      await loadLeaveRequests();
      
      // Load leave balance
      await loadLeaveBalance();
      
      // Load team leaves
      await loadTeamLeaves();

    } catch (error) {
      console.error('Initialize leave management error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [defaultLeaveTypes]);

  // Load leave requests
  const loadLeaveRequests = useCallback(async () => {
    try {
      // Try API first
      let requests = null;
      try {
        const resp = await apiRequest('leave-requests');
        if (resp.success && resp.data) {
          requests = JSON.stringify(resp.data);
          await AsyncStorage.setItem('leave_requests', requests);
        }
      } catch (apiErr) {
        requests = await AsyncStorage.getItem('leave_requests');
      }
      if (requests) {
        const parsed = JSON.parse(requests);
        // Sort by submission date (newest first)
        parsed.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
        setLeaveRequests(parsed);
      } else {
        // Initialize with sample data
        const sampleRequests = [
          {
            id: '1',
            type: 'annual',
            startDate: '2024-12-20',
            endDate: '2024-12-27',
            reason: 'Christmas holiday with family',
            status: 'approved',
            submittedAt: '2024-12-01T10:00:00Z',
            approvedAt: '2024-12-02T14:30:00Z',
            approvedBy: 'Sarah Johnson',
            days: 6,
            emergencyContact: '+44 7700 900124',
            handoverNotes: 'All projects handed over to Tom Wilson',
          },
          {
            id: '2',
            type: 'sick',
            startDate: '2024-11-15',
            endDate: '2024-11-16',
            reason: 'Flu symptoms',
            status: 'approved',
            submittedAt: '2024-11-15T08:00:00Z',
            approvedAt: '2024-11-15T08:15:00Z',
            approvedBy: 'Auto-approved',
            days: 2,
            emergencyContact: '+44 7700 900124',
          },
        ];
        setLeaveRequests(sampleRequests);
        await AsyncStorage.setItem('leave_requests', JSON.stringify(sampleRequests));
        try { await apiRequest('leave-requests', { method: 'POST', body: { requests: sampleRequests } }); } catch(e) {}
      }
    } catch (error) {
      console.error('Load leave requests error:', error);
    }
  }, []);

  // Load leave balance
  const loadLeaveBalance = useCallback(async () => {
    try {
      // Try API first
      let balance = null;
      try {
        const resp = await apiRequest('leave-balance');
        if (resp.success && resp.data) {
          balance = JSON.stringify(resp.data);
          await AsyncStorage.setItem('leave_balance', balance);
        }
      } catch (apiErr) {
        balance = await AsyncStorage.getItem('leave_balance');
      }
      if (balance) {
        setLeaveBalance(JSON.parse(balance));
      } else {
        // Initialize with default balance
        const defaultBalance = {
          annual: { total: 25, used: 8, remaining: 17 },
          sick: { total: 10, used: 2, remaining: 8 },
          personal: { total: 5, used: 0, remaining: 5 },
          emergency: { total: 3, used: 0, remaining: 3 },
          maternity: { total: 90, used: 0, remaining: 90 },
          bereavement: { total: 5, used: 0, remaining: 5 },
        };
        setLeaveBalance(defaultBalance);
        await AsyncStorage.setItem('leave_balance', JSON.stringify(defaultBalance));
        try { await apiRequest('leave-balance', { method: 'POST', body: defaultBalance }); } catch(e) {}
      }
    } catch (error) {
      console.error('Load leave balance error:', error);
    }
  }, []);

  // Load team leaves
  const loadTeamLeaves = useCallback(async () => {
    try {
      // Try API first
      let teamData = null;
      try {
        const resp = await apiRequest('team-leaves');
        if (resp.success && resp.data) {
          teamData = JSON.stringify(resp.data);
          await AsyncStorage.setItem('team_leaves', teamData);
        }
      } catch (apiErr) {
        teamData = await AsyncStorage.getItem('team_leaves');
      }
      if (teamData) {
        setTeamLeaves(JSON.parse(teamData));
      } else {
        // Initialize with sample team data
        const sampleTeamLeaves = [
          {
            id: '1',
            employeeName: 'Tom Wilson',
            type: 'annual',
            startDate: '2024-12-23',
            endDate: '2024-12-30',
            status: 'approved',
            days: 6,
          },
          {
            id: '2',
            employeeName: 'Emma Davis',
            type: 'sick',
            startDate: '2024-12-10',
            endDate: '2024-12-11',
            status: 'approved',
            days: 2,
          },
          {
            id: '3',
            employeeName: 'James Brown',
            type: 'personal',
            startDate: '2024-12-15',
            endDate: '2024-12-15',
            status: 'pending',
            days: 1,
          },
        ];
        setTeamLeaves(sampleTeamLeaves);
        await AsyncStorage.setItem('team_leaves', JSON.stringify(sampleTeamLeaves));
        try { await apiRequest('team-leaves', { method: 'POST', body: { leaves: sampleTeamLeaves } }); } catch(e) {}
      }
    } catch (error) {
      console.error('Load team leaves error:', error);
    }
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await initializeLeaveManagement();
    } catch (error) {
      console.error('Refresh error:', error);
    } finally {
      setRefreshing(false);
    }
  }, [initializeLeaveManagement]);

  // Calculate days between dates
  const calculateDays = useCallback((startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  }, []);

  // Submit leave request
  const submitLeaveRequest = useCallback(async () => {
    try {
      // Validation
      if (!newRequest.type) {
        Alert.alert('Error', 'Please select a leave type');
        return;
      }

      if (!newRequest.reason.trim()) {
        Alert.alert('Error', 'Please provide a reason for your leave');
        return;
      }

      if (new Date(newRequest.startDate) > new Date(newRequest.endDate)) {
        Alert.alert('Error', 'End date must be after start date');
        return;
      }

      const leaveType = leaveTypes.find(type => type.id === newRequest.type);
      const days = calculateDays(newRequest.startDate, newRequest.endDate);

      // Check advance notice
      const today = new Date();
      const startDate = new Date(newRequest.startDate);
      const daysUntilStart = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));

      if (daysUntilStart < leaveType.advanceNotice) {
        Alert.alert(
          'Advance Notice Required',
          `This leave type requires ${leaveType.advanceNotice} days advance notice. Your request may be subject to additional approval.`,
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Submit Anyway', onPress: () => processSubmission() },
          ]
        );
        return;
      }

      await processSubmission();

    } catch (error) {
      console.error('Submit leave request error:', error);
      Alert.alert('Error', 'Failed to submit leave request. Please try again.');
    }
  }, [newRequest, leaveTypes, calculateDays]);

  // Process leave request submission
  const processSubmission = useCallback(async () => {
    try {
      setIsSubmitting(true);

      const leaveType = leaveTypes.find(type => type.id === newRequest.type);
      const days = calculateDays(newRequest.startDate, newRequest.endDate);

      // Check leave balance
      const currentBalance = leaveBalance[newRequest.type];
      if (currentBalance && days > currentBalance.remaining) {
        Alert.alert(
          'Insufficient Leave Balance',
          `You only have ${currentBalance.remaining} days remaining for ${leaveType.name}. This request requires ${days} days.`,
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Submit Anyway', onPress: () => continueSubmission() },
          ]
        );
        return;
      }

      await continueSubmission();

    } catch (error) {
      console.error('Process submission error:', error);
      Alert.alert('Error', 'Failed to process leave request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [newRequest, leaveTypes, calculateDays, leaveBalance]);

  // Continue with submission
  const continueSubmission = useCallback(async () => {
    try {
      const leaveType = leaveTypes.find(type => type.id === newRequest.type);
      const days = calculateDays(newRequest.startDate, newRequest.endDate);

      const request = {
        id: Date.now().toString(),
        ...newRequest,
        startDate: newRequest.startDate.toISOString().split('T')[0],
        endDate: newRequest.endDate.toISOString().split('T')[0],
        days,
        status: leaveType.requiresApproval ? 'pending' : 'approved',
        submittedAt: new Date().toISOString(),
        approvedAt: leaveType.requiresApproval ? null : new Date().toISOString(),
        approvedBy: leaveType.requiresApproval ? null : 'Auto-approved',
      };

      // Add to requests
      const updatedRequests = [request, ...leaveRequests];
      setLeaveRequests(updatedRequests);
      try { await apiRequest('leave-requests', { method: 'PUT', body: { requests: updatedRequests } }); } catch(e) { console.warn('API save failed:', e.message); }
      await AsyncStorage.setItem('leave_requests', JSON.stringify(updatedRequests));

      // Update leave balance if approved
      if (!leaveType.requiresApproval) {
        const updatedBalance = { ...leaveBalance };
        if (updatedBalance[newRequest.type]) {
          updatedBalance[newRequest.type].used += days;
          updatedBalance[newRequest.type].remaining -= days;
          setLeaveBalance(updatedBalance);
          try { await apiRequest('leave-balance', { method: 'PUT', body: updatedBalance }); } catch(e) {}
          await AsyncStorage.setItem('leave_balance', JSON.stringify(updatedBalance));
        }
      }

      // Sync to server
      await syncLeaveRequestToServer(request);

      // Reset form
      setNewRequest({
        type: '',
        startDate: new Date(),
        endDate: new Date(),
        reason: '',
        emergencyContact: '',
        handoverNotes: '',
        attachments: [],
      });

      setShowNewRequestModal(false);

      Alert.alert(
        'Success',
        leaveType.requiresApproval 
          ? 'Leave request submitted successfully! You will be notified once it\'s reviewed.'
          : 'Leave request approved automatically!'
      );

    } catch (error) {
      console.error('Continue submission error:', error);
      Alert.alert('Error', 'Failed to submit leave request. Please try again.');
    }
  }, [newRequest, leaveTypes, calculateDays, leaveRequests, leaveBalance]);

  // Sync leave request to server
  const syncLeaveRequestToServer = useCallback(async (request) => {
    try {
      // Sync leave request via API
      await apiRequest('leave-requests', { method: 'POST', body: request });
    } catch (error) {
      console.error('Sync to server error:', error);
      // Store for later sync when online
      const pendingSync = await AsyncStorage.getItem('pending_leave_sync') || '[]';
      const pending = JSON.parse(pendingSync);
      pending.push(request);
      await AsyncStorage.setItem('pending_leave_sync', JSON.stringify(pending));
    }
  }, []);

  // Cancel leave request
  const cancelLeaveRequest = useCallback(async (requestId) => {
    try {
      Alert.alert(
        'Cancel Leave Request',
        'Are you sure you want to cancel this leave request?',
        [
          { text: 'No', style: 'cancel' },
          {
            text: 'Yes, Cancel',
            style: 'destructive',
            onPress: async () => {
              const updatedRequests = leaveRequests.map(request => 
                request.id === requestId 
                  ? { ...request, status: 'cancelled', cancelledAt: new Date().toISOString() }
                  : request
              );
              
              setLeaveRequests(updatedRequests);
              try { await apiRequest('leave-requests', { method: 'PUT', body: { requests: updatedRequests } }); } catch(e) {}
              await AsyncStorage.setItem('leave_requests', JSON.stringify(updatedRequests));
              
              Alert.alert('Success', 'Leave request cancelled successfully');
            },
          },
        ]
      );
    } catch (error) {
      console.error('Cancel leave request error:', error);
      Alert.alert('Error', 'Failed to cancel leave request. Please try again.');
    }
  }, [leaveRequests]);

  // Get status color
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'approved':
        return '#10B981';
      case 'pending':
        return '#F59E0B';
      case 'rejected':
        return '#EF4444';
      case 'cancelled':
        return '#64748B';
      default:
        return '#64748B';
    }
  }, []);

  // Get status icon
  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'approved':
        return 'checkmark-circle';
      case 'pending':
        return 'time-outline';
      case 'rejected':
        return 'close-circle';
      case 'cancelled':
        return 'ban-outline';
      default:
        return 'help-circle';
    }
  }, []);

  // Format date
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, []);

  // Render leave request item
  const renderLeaveRequest = useCallback(({ item }) => {
    const leaveType = leaveTypes.find(type => type.id === item.type);
    
    return (
      <TouchableOpacity
        style={styles.requestCard}
        onPress={() => {
          setSelectedLeave(item);
          setShowDetailsModal(true);
        }}
      >
        <View style={styles.requestHeader}>
          <View style={styles.requestType}>
            <MaterialCommunityIcons 
              name={leaveType?.icon || 'calendar-outline'} 
              size={24} 
              color={leaveType?.color || '#4A90E2'} 
            />
            <Text style={styles.requestTypeName}>{leaveType?.name || 'Unknown'}</Text>
          </View>
          
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
            <Ionicons name={getStatusIcon(item.status)} size={16} color="#FFFFFF" />
            <Text style={styles.statusText}>{String(item.status ?? '').toUpperCase()}</Text>
          </View>
        </View>
        
        <View style={styles.requestDetails}>
          <View style={styles.requestDates}>
            <Text style={styles.requestDatesText}>
              {formatDate(item.startDate)} - {formatDate(item.endDate)}
            </Text>
            <Text style={styles.requestDays}>{item.days} day{item.days !== 1 ? 's' : ''}</Text>
          </View>
          
          <Text style={styles.requestReason} numberOfLines={2}>
            {item.reason}
          </Text>
          
          <Text style={styles.requestSubmitted}>
            Submitted {new Date(item.submittedAt).toLocaleDateString()}
          </Text>
        </View>
      </TouchableOpacity>
    );
  }, [leaveTypes, getStatusColor, getStatusIcon, formatDate]);

  // Render balance card
  const renderBalanceCard = useCallback((leaveType) => {
    const balance = leaveBalance[leaveType.id] || { total: 0, used: 0, remaining: 0 };
    const percentage = balance.total > 0 ? (balance.used / balance.total) * 100 : 0;
    
    return (
      <View key={leaveType.id} style={styles.balanceCard}>
        <View style={styles.balanceHeader}>
          <MaterialCommunityIcons 
            name={leaveType.icon} 
            size={32} 
            color={leaveType.color} 
          />
          <Text style={styles.balanceTitle}>{leaveType.name}</Text>
        </View>
        
        <View style={styles.balanceStats}>
          <View style={styles.balanceStat}>
            <Text style={styles.balanceNumber}>{balance.remaining}</Text>
            <Text style={styles.balanceLabel}>Remaining</Text>
          </View>
          
          <View style={styles.balanceStat}>
            <Text style={styles.balanceNumber}>{balance.used}</Text>
            <Text style={styles.balanceLabel}>Used</Text>
          </View>
          
          <View style={styles.balanceStat}>
            <Text style={styles.balanceNumber}>{balance.total}</Text>
            <Text style={styles.balanceLabel}>Total</Text>
          </View>
        </View>
        
        <View style={styles.balanceProgress}>
          <View style={styles.balanceProgressBar}>
            <View 
              style={[
                styles.balanceProgressFill,
                { 
                  width: `${percentage}%`,
                  backgroundColor: leaveType.color,
                }
              ]} 
            />
          </View>
          <Text style={styles.balancePercentage}>{Math.round(percentage)}% used</Text>
        </View>
      </View>
    );
  }, [leaveBalance]);

  // Render team leave item
  const renderTeamLeave = useCallback(({ item }) => {
    const leaveType = leaveTypes.find(type => type.id === item.type);
    
    return (
      <View style={styles.teamLeaveCard}>
        <View style={styles.teamLeaveHeader}>
          <Text style={styles.teamEmployeeName}>{item.employeeName}</Text>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
            <Text style={styles.statusText}>{String(item.status ?? '').toUpperCase()}</Text>
          </View>
        </View>
        
        <View style={styles.teamLeaveDetails}>
          <MaterialCommunityIcons 
            name={leaveType?.icon || 'calendar-outline'} 
            size={20} 
            color={leaveType?.color || '#4A90E2'} 
          />
          <Text style={styles.teamLeaveType}>{leaveType?.name || 'Unknown'}</Text>
        </View>
        
        <Text style={styles.teamLeaveDates}>
          {formatDate(item.startDate)} - {formatDate(item.endDate)} ({item.days} day{item.days !== 1 ? 's' : ''})
        </Text>
      </View>
    );
  }, [leaveTypes, getStatusColor, formatDate]);

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'requests':
        return (
          <View style={styles.tabContent}>
            <FlatList
              data={leaveRequests}
              renderItem={renderLeaveRequest}
              keyExtractor={(item) => item.id}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.requestsList}
              ListEmptyComponent={
                <View style={styles.emptyState}>
                  <MaterialCommunityIcons name="calendar-outline" size={64} color="#94A3B8" />
                  <Text style={styles.emptyStateTitle}>No Leave Requests</Text>
                  <Text style={styles.emptyStateDescription}>
                    You haven't submitted any leave requests yet
                  </Text>
                </View>
              }
            />
          </View>
        );

      case 'balance':
        return (
          <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}>
            <View style={styles.balanceGrid}>
              {leaveTypes.map(renderBalanceCard)}
            </View>
          </ScrollView>
        );

      case 'team':
        return (
          <View style={styles.tabContent}>
            <FlatList
              data={teamLeaves}
              renderItem={renderTeamLeave}
              keyExtractor={(item) => item.id}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.teamLeavesList}
              ListEmptyComponent={
                <View style={styles.emptyState}>
                  <MaterialCommunityIcons name="account-group" size={64} color="#94A3B8" />
                  <Text style={styles.emptyStateTitle}>No Team Leaves</Text>
                  <Text style={styles.emptyStateDescription}>
                    No team members have scheduled leaves
                  </Text>
                </View>
              }
            />
          </View>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#4A90E2" />
          <Text style={styles.loadingText}>Loading Leave Management...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#4A90E2" />
      
      {/* Header */}
      <LinearGradient
        colors={['#4A90E2', '#357ABD']}
        style={styles.header}
      >
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <Text style={styles.headerTitle}>Leave Management</Text>
        
        <TouchableOpacity
          style={styles.addButton}
          onPress={() => setShowNewRequestModal(true)}
        >
          <MaterialCommunityIcons name="plus" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </LinearGradient>

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {[
            { key: 'requests', label: 'My Requests', icon: 'file-document-outline' },
            { key: 'balance', label: 'Leave Balance', icon: 'chart-pie' },
            { key: 'team', label: 'Team Calendar', icon: 'account-group' },
          ].map((tab) => (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, activeTab === tab.key && styles.activeTab]}
              onPress={() => setActiveTab(tab.key)}
            >
              <MaterialCommunityIcons
                name={tab.icon}
                size={20}
                color={activeTab === tab.key ? '#4A90E2' : '#64748B'}
              />
              <Text
                style={[
                  styles.tabText,
                  activeTab === tab.key && styles.activeTabText,
                ]}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Content */}
      <View style={styles.content}>
        {renderTabContent()}
      </View>

      {/* New Request Modal */}
      <Modal
        visible={showNewRequestModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowNewRequestModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>New Leave Request</Text>
            <TouchableOpacity onPress={() => setShowNewRequestModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
            {/* Leave Type Selection */}
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Leave Type *</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.leaveTypeOptions}>
                  {leaveTypes.map((type) => (
                    <TouchableOpacity
                      key={type.id}
                      style={[
                        styles.leaveTypeOption,
                        newRequest.type === type.id && styles.leaveTypeOptionSelected,
                        { borderColor: type.color }
                      ]}
                      onPress={() => setNewRequest(prev => ({ ...prev, type: type.id }))}
                    >
                      <MaterialCommunityIcons 
                        name={type.icon} 
                        size={24} 
                        color={newRequest.type === type.id ? type.color : '#64748B'} 
                      />
                      <Text style={[
                        styles.leaveTypeOptionText,
                        newRequest.type === type.id && { color: type.color }
                      ]}>
                        {type.name}
                      </Text>
                      {type.requiresApproval && (
                        <Text style={styles.leaveTypeRequiresApproval}>Requires Approval</Text>
                      )}
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Date Selection */}
            <View style={styles.dateRow}>
              <View style={styles.dateGroup}>
                <Text style={styles.inputLabel}>Start Date *</Text>
                <TouchableOpacity
                  style={styles.dateInput}
                  onPress={() => setShowStartDatePicker(true)}
                >
                  <Text style={styles.dateInputText}>
                    {newRequest.startDate.toLocaleDateString()}
                  </Text>
                  <MaterialCommunityIcons name="calendar" size={20} color="#64748B" />
                </TouchableOpacity>
              </View>
              
              <View style={styles.dateGroup}>
                <Text style={styles.inputLabel}>End Date *</Text>
                <TouchableOpacity
                  style={styles.dateInput}
                  onPress={() => setShowEndDatePicker(true)}
                >
                  <Text style={styles.dateInputText}>
                    {newRequest.endDate.toLocaleDateString()}
                  </Text>
                  <MaterialCommunityIcons name="calendar" size={20} color="#64748B" />
                </TouchableOpacity>
              </View>
            </View>

            {/* Days Calculation */}
            <View style={styles.daysCalculation}>
              <Text style={styles.daysCalculationText}>
                Total Days: {calculateDays(newRequest.startDate, newRequest.endDate)}
              </Text>
            </View>

            {/* Reason */}
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Reason *</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={newRequest.reason}
                onChangeText={(text) => setNewRequest(prev => ({ ...prev, reason: text }))}
                placeholder="Please provide a reason for your leave request"
                multiline
                numberOfLines={4}
              />
            </View>

            {/* Emergency Contact */}
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Emergency Contact</Text>
              <TextInput
                style={styles.input}
                value={newRequest.emergencyContact}
                onChangeText={(text) => setNewRequest(prev => ({ ...prev, emergencyContact: text }))}
                placeholder="Emergency contact number"
                keyboardType="phone-pad"
              />
            </View>

            {/* Handover Notes */}
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Handover Notes</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={newRequest.handoverNotes}
                onChangeText={(text) => setNewRequest(prev => ({ ...prev, handoverNotes: text }))}
                placeholder="Any important handover information for colleagues"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Submit Button */}
            <TouchableOpacity
              style={[styles.submitButton, (!newRequest.type || !newRequest.reason.trim()) && styles.submitButtonDisabled]}
              onPress={submitLeaveRequest}
              disabled={isSubmitting || !newRequest.type || !newRequest.reason.trim()}
            >
              {isSubmitting ? (
                <ActivityIndicator size={20} color="#FFFFFF" />
              ) : (
                <>
                  <MaterialCommunityIcons name="send" size={20} color="#FFFFFF" />
                  <Text style={styles.submitButtonText}>Submit Request</Text>
                </>
              )}
            </TouchableOpacity>
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Leave Details Modal */}
      <Modal
        visible={showDetailsModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowDetailsModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Leave Request Details</Text>
            <TouchableOpacity onPress={() => setShowDetailsModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          
          {selectedLeave && (
            <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
              {/* Status and Type */}
              <View style={styles.detailsHeader}>
                <View style={styles.detailsType}>
                  <MaterialCommunityIcons 
                    name={leaveTypes.find(t => t.id === selectedLeave.type)?.icon || 'calendar-outline'} 
                    size={32} 
                    color={leaveTypes.find(t => t.id === selectedLeave.type)?.color || '#4A90E2'} 
                  />
                  <Text style={styles.detailsTypeName}>
                    {leaveTypes.find(t => t.id === selectedLeave.type)?.name || 'Unknown'}
                  </Text>
                </View>
                
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(selectedLeave.status) }]}>
                  <Ionicons name={getStatusIcon(selectedLeave.status)} size={16} color="#FFFFFF" />
                  <Text style={styles.statusText}>{String(selectedLeave.status ?? '').toUpperCase()}</Text>
                </View>
              </View>

              {/* Dates and Duration */}
              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>Duration</Text>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Start Date:</Text>
                  <Text style={styles.detailsValue}>{formatDate(selectedLeave.startDate)}</Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>End Date:</Text>
                  <Text style={styles.detailsValue}>{formatDate(selectedLeave.endDate)}</Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Total Days:</Text>
                  <Text style={styles.detailsValue}>{selectedLeave.days} day{selectedLeave.days !== 1 ? 's' : ''}</Text>
                </View>
              </View>

              {/* Reason */}
              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>Reason</Text>
                <Text style={styles.detailsText}>{selectedLeave.reason}</Text>
              </View>

              {/* Emergency Contact */}
              {selectedLeave.emergencyContact && (
                <View style={styles.detailsSection}>
                  <Text style={styles.detailsSectionTitle}>Emergency Contact</Text>
                  <Text style={styles.detailsText}>{selectedLeave.emergencyContact}</Text>
                </View>
              )}

              {/* Handover Notes */}
              {selectedLeave.handoverNotes && (
                <View style={styles.detailsSection}>
                  <Text style={styles.detailsSectionTitle}>Handover Notes</Text>
                  <Text style={styles.detailsText}>{selectedLeave.handoverNotes}</Text>
                </View>
              )}

              {/* Approval Information */}
              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>Request Information</Text>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Submitted:</Text>
                  <Text style={styles.detailsValue}>
                    {new Date(selectedLeave.submittedAt).toLocaleDateString()}
                  </Text>
                </View>
                {selectedLeave.approvedAt && (
                  <>
                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Approved:</Text>
                      <Text style={styles.detailsValue}>
                        {new Date(selectedLeave.approvedAt).toLocaleDateString()}
                      </Text>
                    </View>
                    <View style={styles.detailsRow}>
                      <Text style={styles.detailsLabel}>Approved By:</Text>
                      <Text style={styles.detailsValue}>{selectedLeave.approvedBy}</Text>
                    </View>
                  </>
                )}
              </View>

              {/* Actions */}
              {selectedLeave.status === 'pending' && (
                <TouchableOpacity
                  style={styles.cancelButton}
                  onPress={() => {
                    setShowDetailsModal(false);
                    cancelLeaveRequest(selectedLeave.id);
                  }}
                >
                  <MaterialCommunityIcons name="cancel" size={20} color="#EF4444" />
                  <Text style={styles.cancelButtonText}>Cancel Request</Text>
                </TouchableOpacity>
              )}
            </ScrollView>
          )}
        </SafeAreaView>
      </Modal>

      {/* Date Pickers */}
      {showStartDatePicker && (
        <DateTimePicker
          value={newRequest.startDate}
          mode="date"
          display="default"
          onChange={(event, selectedDate) => {
            setShowStartDatePicker(false);
            if (selectedDate) {
              setNewRequest(prev => ({ ...prev, startDate: selectedDate }));
            }
          }}
          minimumDate={new Date()}
        />
      )}

      {showEndDatePicker && (
        <DateTimePicker
          value={newRequest.endDate}
          mode="date"
          display="default"
          onChange={(event, selectedDate) => {
            setShowEndDatePicker(false);
            if (selectedDate) {
              setNewRequest(prev => ({ ...prev, endDate: selectedDate }));
            }
          }}
          minimumDate={newRequest.startDate}
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: Platform.OS === 'ios' ? 50 : 16,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  addButton: {
    padding: 8,
  },
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    marginRight: 8,
  },
  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: '#4A90E2',
  },
  tabText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  activeTabText: {
    color: '#4A90E2',
  },
  content: {
    flex: 1,
  },
  tabContent: {
    flex: 1,
  },
  requestsList: {
    padding: 20,
  },
  requestCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  requestHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  requestType: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  requestTypeName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginLeft: 8,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  requestDetails: {
    gap: 8,
  },
  requestDates: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  requestDatesText: {
    fontSize: 14,
    color: '#0F172A',
    fontWeight: '500',
  },
  requestDays: {
    fontSize: 12,
    color: '#64748B',
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
  },
  requestReason: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
  requestSubmitted: {
    fontSize: 12,
    color: '#94A3B8',
  },
  balanceGrid: {
    padding: 20,
    gap: 16,
  },
  balanceCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  balanceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  balanceTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginLeft: 12,
  },
  balanceStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  balanceStat: {
    alignItems: 'center',
  },
  balanceNumber: {
    fontSize: 24,
    fontWeight: '700',
    color: '#0F172A',
  },
  balanceLabel: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 4,
  },
  balanceProgress: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  balanceProgressBar: {
    flex: 1,
    height: 8,
    backgroundColor: '#E2E8F0',
    borderRadius: 4,
    marginRight: 12,
    overflow: 'hidden',
  },
  balanceProgressFill: {
    height: '100%',
    borderRadius: 4,
  },
  balancePercentage: {
    fontSize: 12,
    color: '#64748B',
    fontWeight: '500',
  },
  teamLeavesList: {
    padding: 20,
  },
  teamLeaveCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  teamLeaveHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  teamEmployeeName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
  },
  teamLeaveDetails: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  teamLeaveType: {
    fontSize: 14,
    color: '#0F172A',
    marginLeft: 8,
  },
  teamLeaveDates: {
    fontSize: 12,
    color: '#64748B',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginTop: 16,
  },
  emptyStateDescription: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 8,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#0F172A',
  },
  modalContent: {
    padding: 20,
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0F172A',
    backgroundColor: '#FFFFFF',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  leaveTypeOptions: {
    flexDirection: 'row',
    gap: 12,
  },
  leaveTypeOption: {
    alignItems: 'center',
    padding: 16,
    borderWidth: 2,
    borderColor: '#E2E8F0',
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    minWidth: 120,
  },
  leaveTypeOptionSelected: {
    backgroundColor: '#F0F7FF',
  },
  leaveTypeOptionText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#64748B',
    marginTop: 8,
    textAlign: 'center',
  },
  leaveTypeRequiresApproval: {
    fontSize: 10,
    color: '#F59E0B',
    marginTop: 4,
    textAlign: 'center',
  },
  dateRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 20,
  },
  dateGroup: {
    flex: 1,
  },
  dateInput: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
  },
  dateInputText: {
    fontSize: 16,
    color: '#0F172A',
  },
  daysCalculation: {
    alignItems: 'center',
    marginBottom: 20,
  },
  daysCalculationText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#4A90E2',
    backgroundColor: '#F0F7FF',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#4A90E2',
    borderRadius: 12,
    paddingVertical: 16,
    marginTop: 20,
  },
  submitButtonDisabled: {
    backgroundColor: '#94A3B8',
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  detailsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  detailsType: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailsTypeName: {
    fontSize: 20,
    fontWeight: '600',
    color: '#0F172A',
    marginLeft: 12,
  },
  detailsSection: {
    marginBottom: 24,
  },
  detailsSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 12,
  },
  detailsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  detailsLabel: {
    fontSize: 14,
    color: '#64748B',
  },
  detailsValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0F172A',
  },
  detailsText: {
    fontSize: 14,
    color: '#0F172A',
    lineHeight: 20,
  },
  cancelButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#EF4444',
    borderRadius: 12,
    paddingVertical: 16,
    marginTop: 20,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#EF4444',
    marginLeft: 8,
  },
});
